import React, { useEffect, useState } from 'react';
import {
  Badge, Button, DataTable, KV, Modal, PageHead, SearchBox, StatCard, Tabs,
  Textarea, dateStr, inr, num,
} from '../components/ui';
import useList from '../hooks/useList';
import { contractApi } from '../api';
import { useToast } from '../context/ToastContext';

const TABS = [
  { value: '', label: 'All' },
  { value: 'sent', label: 'Awaiting acceptance' },
  { value: 'active', label: 'Active' },
  { value: 'expired,renewed', label: 'Expired / renewed' },
  { value: 'rejected,terminated', label: 'Closed' },
];

const Contracts = () => {
  const toast = useToast();
  const list = useList(contractApi.list, { status: '', search: '' });

  const [stats, setStats] = useState(null);
  const [detail, setDetail] = useState(null);
  const [terminate, setTerminate] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const loadStats = () => contractApi.stats().then(res => setStats(res.data)).catch(() => {});
  useEffect(() => { loadStats(); }, []);

  const runLifecycle = async () => {
    setBusy(true);
    try {
      const res = await contractApi.runLifecycle();
      const d = res.data;
      toast.success(`Activated ${d.activated} · expired ${d.expired} · auto-renewed ${d.autoRenewed} · ${d.remindersSent} reminders sent`);
      list.reload();
      loadStats();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const doTerminate = async () => {
    setBusy(true);
    try {
      await contractApi.terminate(terminate._id, reason);
      toast.success('Contract terminated');
      setTerminate(null);
      setReason('');
      setDetail(null);
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: 'contractNumber',
      label: 'Contract',
      render: row => (
        <>
          <div className="cell-strong mono">{row.contractNumber}</div>
          <div className="cell-muted">{row.title}</div>
        </>
      ),
    },
    {
      key: 'parties',
      label: 'Seller → Buyer',
      render: row => (
        <>
          <div>{row.seller?.name}</div>
          <div className="cell-muted">→ {row.buyer?.name}</div>
        </>
      ),
    },
    { key: 'type', label: 'Type', render: row => <Badge status={row.type} tone="brand" dot={false} /> },
    { key: 'items', label: 'SKUs', align: 'center', render: row => num(row.items?.length || 0) },
    { key: 'totalValue', label: 'Value', align: 'right', render: row => <strong>{inr(row.totalValue, true)}</strong> },
    {
      key: 'validity',
      label: 'Validity',
      render: row => (
        <>
          <div className="text-sm">{dateStr(row.validFrom)} → {dateStr(row.validTill)}</div>
          {row.status === 'active' && <div className="cell-muted">{row.daysRemaining} days left</div>}
        </>
      ),
    },
    { key: 'status', label: 'Status', render: row => <Badge status={row.status} /> },
  ];

  return (
    <>
      <PageHead
        title="Contracts"
        subtitle="Fixed-price supply agreements between sellers and buyers."
        actions={
          <Button variant="ghost" loading={busy} onClick={runLifecycle} title="Activate matured contracts, expire lapsed ones, auto-renew and send reminders">
            ⟳ Run lifecycle
          </Button>
        }
      />

      {stats && (
        <div className="stat-grid stagger mb-2">
          <StatCard accent label="Contracted value" value={inr(stats.totalValue, true)} icon="📄" foot={`${num(stats.total)} contracts`} />
          <StatCard label="Active" value={num(stats.byStatus.active)} icon="✓" />
          <StatCard label="Awaiting acceptance" value={num(stats.byStatus.sent)} icon="⏳" />
          <StatCard label="Expiring in 30 days" value={num(stats.expiringIn30Days)} icon="⚠" />
          <StatCard label="Renewed" value={num(stats.byStatus.renewed)} icon="⟳" />
        </div>
      )}

      <Tabs tabs={TABS} value={list.filters.status} onChange={v => list.setFilter('status', v)} />

      <div className="filters">
        <SearchBox value={list.filters.search} onChange={v => list.setFilter('search', v)} placeholder="Search contract number or title…" />
        <div className="grow" />
        <span className="text-muted text-sm">{list.meta ? `${num(list.meta.total)} contracts` : ''}</span>
      </div>

      <DataTable
        columns={columns}
        rows={list.rows}
        loading={list.loading}
        meta={list.meta}
        onPage={list.setPage}
        onRowClick={async row => {
          try {
            setDetail((await contractApi.get(row._id)).data);
          } catch (err) {
            toast.error(err.message);
          }
        }}
      />

      <Modal
        open={!!detail}
        size="wide"
        title={detail?.contractNumber}
        subtitle={detail?.title}
        onClose={() => setDetail(null)}
        footer={
          detail && ['active', 'accepted', 'sent'].includes(detail.status) && (
            <Button variant="danger" onClick={() => setTerminate(detail)}>Terminate contract</Button>
          )
        }
      >
        {detail && (
          <>
            <div className="row mb-2" style={{ gap: 8 }}>
              <Badge status={detail.status} />
              <Badge status={detail.type} tone="brand" dot={false} />
              {detail.autoRenew && <Badge status="renewed" dot={false}>Auto-renews</Badge>}
            </div>

            <KV
              items={[
                { label: 'Seller', value: `${detail.seller?.name} (${detail.seller?.code})` },
                { label: 'Buyer', value: `${detail.buyer?.name} (${detail.buyer?.code})` },
                { label: 'Valid', value: `${dateStr(detail.validFrom)} → ${dateStr(detail.validTill)}` },
                { label: 'Total value', value: inr(detail.totalValue) },
                { label: 'Payment terms', value: detail.paymentTerms || '—' },
                { label: 'Delivery terms', value: detail.deliveryTerms || '—' },
                detail.acceptedAt && { label: 'Accepted', value: `${dateStr(detail.acceptedAt)} by ${detail.acceptedBy?.name || '—'}` },
                detail.acceptanceSignature && { label: 'Digital signature', value: detail.acceptanceSignature },
                detail.acceptanceIp && { label: 'Signed from IP', value: detail.acceptanceIp },
                detail.rejectionReason && { label: 'Reason', value: detail.rejectionReason },
              ]}
            />

            <div className="divider" />
            <div className="card-title mb-2">Contracted rates</div>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th style={{ textAlign: 'right' }}>Contract rate</th>
                    <th style={{ textAlign: 'right' }}>List price at signing</th>
                    <th style={{ textAlign: 'right' }}>Committed qty</th>
                    <th style={{ textAlign: 'right' }}>Fulfilled</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.items.map(item => (
                    <tr key={item._id}>
                      <td className="cell-strong">{item.product?.name || item.productName}</td>
                      <td style={{ textAlign: 'right' }} className="cell-strong">{inr(item.rate)}</td>
                      <td style={{ textAlign: 'right' }} className="text-muted">{inr(item.listPriceAtSigning)}</td>
                      <td style={{ textAlign: 'right' }}>{num(item.committedQuantity)}</td>
                      <td style={{ textAlign: 'right' }}>{num(item.fulfilledQuantity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {detail.terms && (
              <>
                <div className="divider" />
                <div className="card-title mb-2">Terms</div>
                <p className="text-sm text-muted" style={{ whiteSpace: 'pre-wrap' }}>{detail.terms}</p>
              </>
            )}
          </>
        )}
      </Modal>

      <Modal
        open={!!terminate}
        title="Terminate this contract?"
        onClose={() => setTerminate(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setTerminate(null)}>Cancel</Button>
            <Button variant="danger" loading={busy} onClick={doTerminate}>Terminate</Button>
          </>
        }
      >
        <p className="text-muted mb-2">
          Contracted pricing stops applying to new orders immediately. Existing orders are unaffected.
        </p>
        <Textarea label="Reason" value={reason} onChange={e => setReason(e.target.value)} />
      </Modal>
    </>
  );
};

export default Contracts;
